import { IhightLightItem, IhightLightObj, IPosItem } from './ctype';

export type HighlightAddReason = 'empty' | 'duplicate' | 'third-layer' | 'note-overlap';

export interface HighlightAddResult {
  allowed: boolean;
  reason?: HighlightAddReason;
}

type HighlightEntry = {
  uid: string;
  item: IhightLightItem;
  order: number;
  index: number;
};

const overlaps = (left: IPosItem, right: IPosItem) => (
  left.dataKey === right.dataKey &&
  Math.max(left.startOffset, right.startOffset) < Math.min(left.endOffset, right.endOffset)
);

const covers = (item: IPosItem, offset: number) => (
  item.startOffset <= offset && offset < item.endOffset
);

export const getHighlightPositions = (item: IhightLightItem): IPosItem[] => {
  if (item.posArr && item.posArr.length) {
    return item.posArr;
  }
  return Object.values(item.posObj || {}).reduce<IPosItem[]>((all, positions) => all.concat(positions), []);
};

export const normalizeHighlightPositions = (positions: IPosItem[]): IPosItem[] => {
  const sorted = positions
    .filter((item) => item.dataKey && Number.isFinite(item.startOffset) && Number.isFinite(item.endOffset) && item.endOffset > item.startOffset)
    .map((item) => ({...item}))
    .sort((left, right) => (
      left.dataKey.localeCompare(right.dataKey) ||
      left.startOffset - right.startOffset ||
      left.endOffset - right.endOffset
    ));

  return sorted.reduce<IPosItem[]>((merged, item) => {
    const previous = merged[merged.length - 1];
    if (previous && previous.dataKey === item.dataKey && previous.endOffset >= item.startOffset) {
      previous.endOffset = Math.max(previous.endOffset, item.endOffset);
      return merged;
    }
    merged.push(item);
    return merged;
  }, []);
};

const getEntries = (highLightObj: IhightLightObj): HighlightEntry[] => (
  Object.keys(highLightObj || {}).map((key, index) => {
    const item = highLightObj[key];
    return {
      uid: item.uid || key,
      item,
      order: typeof item.createdAt === 'number' ? item.createdAt : index,
      index,
    };
  })
);

const getTopEntry = (entries: HighlightEntry[]) => {
  const notes = entries.filter(({item}) => Boolean(item.panelData));
  const available = notes.length ? notes : entries;
  return available.reduce<HighlightEntry | undefined>((top, entry) => {
    if (!top || entry.order > top.order || (entry.order === top.order && entry.index > top.index)) {
      return entry;
    }
    return top;
  }, undefined);
};

const hasSamePositions = (left: IPosItem[], right: IPosItem[]) => {
  const normalizedLeft = normalizeHighlightPositions(left);
  const normalizedRight = normalizeHighlightPositions(right);
  return normalizedLeft.length === normalizedRight.length && normalizedLeft.every((item, index) => {
    const target = normalizedRight[index];
    return item.dataKey === target.dataKey &&
      item.startOffset === target.startOffset &&
      item.endOffset === target.endOffset;
  });
};

const reachesThirdOrdinaryLayer = (entries: HighlightEntry[], candidate: IPosItem[]) => {
  const ordinaryEntries = entries.filter(({item}) => !item.panelData);

  return candidate.some((candidatePosition) => {
    const ranges = ordinaryEntries.reduce<Array<{uid: string; position: IPosItem;}>>((all, entry) => {
      getHighlightPositions(entry.item).forEach((position) => {
        if (overlaps(position, candidatePosition)) {
          all.push({uid: entry.uid, position});
        }
      });
      return all;
    }, []);

    const boundaries = Array.from(new Set([
      candidatePosition.startOffset,
      candidatePosition.endOffset,
      ...ranges.reduce<number[]>((all, {position}) => all.concat([
        Math.max(candidatePosition.startOffset, position.startOffset),
        Math.min(candidatePosition.endOffset, position.endOffset),
      ]), []),
    ])).sort((left, right) => left - right);

    for (let index = 0; index < boundaries.length - 1; index++) {
      if (boundaries[index] === boundaries[index + 1]) {
        continue;
      }
      const probe = (boundaries[index] + boundaries[index + 1]) / 2;
      const activeUids = new Set(
        ranges.filter(({position}) => covers(position, probe)).map(({uid}) => uid)
      );
      if (activeUids.size >= 2) {
        return true;
      }
    }
    return false;
  });
};

export const canAddHighlight = (
  highLightObj: IhightLightObj,
  positions: IPosItem[],
  needNote: boolean
): HighlightAddResult => {
  const candidate = normalizeHighlightPositions(positions);
  if (!candidate.length) {
    return {allowed: false, reason: 'empty'};
  }

  const entries = getEntries(highLightObj);
  const sameKindDuplicate = entries.some(({item}) => (
    Boolean(item.panelData) === needNote && hasSamePositions(getHighlightPositions(item), candidate)
  ));
  if (sameKindDuplicate) {
    return {allowed: false, reason: 'duplicate'};
  }

  if (needNote) {
    const overlapsNote = entries.some(({item}) => (
      Boolean(item.panelData) && getHighlightPositions(item).some((saved) => candidate.some((next) => overlaps(saved, next)))
    ));
    return overlapsNote ? {allowed: false, reason: 'note-overlap'} : {allowed: true};
  }

  return reachesThirdOrdinaryLayer(entries, candidate)
    ? {allowed: false, reason: 'third-layer'}
    : {allowed: true};
};

export const getClearHighlightRanges = (highLightObj: IhightLightObj, positions: IPosItem[]) => {
  const candidate = normalizeHighlightPositions(positions);
  const entries = getEntries(highLightObj);
  const clearRanges: Record<string, IPosItem[]> = {};

  candidate.forEach((selected) => {
    const intersecting = entries.reduce<Array<{entry: HighlightEntry; position: IPosItem;}>>((all, entry) => {
      getHighlightPositions(entry.item).forEach((position) => {
        if (overlaps(position, selected)) {
          all.push({entry, position});
        }
      });
      return all;
    }, []);
    const boundaries = Array.from(new Set([
      selected.startOffset,
      selected.endOffset,
      ...intersecting.reduce<number[]>((all, {position}) => all.concat([
        Math.max(selected.startOffset, position.startOffset),
        Math.min(selected.endOffset, position.endOffset),
      ]), []),
    ])).sort((left, right) => left - right);

    for (let index = 0; index < boundaries.length - 1; index++) {
      const startOffset = boundaries[index];
      const endOffset = boundaries[index + 1];
      if (startOffset === endOffset) {
        continue;
      }
      const probe = (startOffset + endOffset) / 2;
      const activeEntries = intersecting.reduce<HighlightEntry[]>((all, {entry, position}) => {
        if (covers(position, probe) && !all.some(({uid}) => uid === entry.uid)) {
          all.push(entry);
        }
        return all;
      }, []);
      const top = getTopEntry(activeEntries);
      if (!top) {
        continue;
      }
      if (!clearRanges[top.uid]) {
        clearRanges[top.uid] = [];
      }
      clearRanges[top.uid].push({
        dataKey: selected.dataKey,
        startOffset,
        endOffset,
        uid: top.uid,
        hasPanel: Boolean(top.item.panelData),
      });
    }
  });

  Object.keys(clearRanges).forEach((uid) => {
    clearRanges[uid] = normalizeHighlightPositions(clearRanges[uid]);
  });
  return clearRanges;
};

export const subtractHighlightPositions = (positions: IPosItem[], removals: IPosItem[]) => {
  const normalizedRemovals = normalizeHighlightPositions(removals);
  const remaining = positions.reduce<IPosItem[]>((all, position) => {
    const matchingRemovals = normalizedRemovals.filter(({dataKey}) => dataKey === position.dataKey);
    const fragments = matchingRemovals.reduce<IPosItem[]>((current, removal) => (
      current.reduce<IPosItem[]>((next, fragment) => {
        if (removal.endOffset <= fragment.startOffset || removal.startOffset >= fragment.endOffset) {
          next.push(fragment);
          return next;
        }
        if (fragment.startOffset < removal.startOffset) {
          next.push({...fragment, endOffset: removal.startOffset});
        }
        if (removal.endOffset < fragment.endOffset) {
          next.push({...fragment, startOffset: removal.endOffset});
        }
        return next;
      }, [])
    ), [{...position}]);
    return all.concat(fragments);
  }, []);

  return normalizeHighlightPositions(remaining);
};
